import React, { useEffect, useState } from 'react';
import {
    TableBody,
    TableRow,
    TableCell,
    TextField,
    Tooltip,
    Box,
} from '@material-ui/core';
import { Checkbox } from '@paro.io/base-ui';
import { IconInfoCircle } from '@paro.io/base-icons';

const EditableTableBody = ({
    importedClients,
    page,
    rowsPerPage,
    setErrorInClients,
    isEditable,
    selectedRows = [],
    onRowCheckbox,
    setErrorInSelectedClients,
}: any) => {

    const [clients, setClients] = useState(importedClients || []);
    const [editIdx, setEditIdx] = useState(null);
    const [editField, setEditField] = useState(null);

    useEffect(() => {
        setClients(importedClients);
    }, [importedClients, importedClients.length]);

    useEffect(() => {
        const hasErrors = clients.some((client: any) => {
            const isEmailInvalid = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(client.email || '');
            return (
                !client.firstName?.trim() ||
                !client.lastName?.trim() ||
                !client.company?.trim() ||
                !client.email?.trim() ||
                isEmailInvalid
            );
        });
        setErrorInClients(hasErrors);

        if (selectedRows.length > 0) {
            const hasErrors = selectedRows.map(idx => importedClients[idx])?.some((client: any) => {
                const isEmailInvalid = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(client.email || '');
                return (
                    !client.firstName?.trim() ||
                    !client.lastName?.trim() ||
                    !client.company?.trim() ||
                    !client.email?.trim() ||
                    isEmailInvalid
                );
            });
            setErrorInSelectedClients(hasErrors);
        }
        
    }, [clients, setErrorInClients, selectedRows, setErrorInSelectedClients, importedClients]);

    const handleChange = (e: any, idx: number, field: string) => {
        const newClients = [...clients];
        newClients[idx][field] = e.target.value;
        setClients(newClients);
    };

    const startEditing = (idx: any, field: any) => {
        setEditIdx(idx);
        setEditField(field);
    };

    const stopEditing = () => {
        setEditIdx(null);
        setEditField(null);
    };

    return (
        <TableBody>
            {clients
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((client: any, idx: number) => {
                    const realIdx = page * rowsPerPage + idx;
                    const isRowChecked = selectedRows.includes(realIdx);
                    return (
                        <TableRow key={realIdx}>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    id={`checkbox-single-${realIdx}`}
                                    key={`checkbox-single-${realIdx}`}
                                    isChecked={isRowChecked}
                                    onChange={() => onRowCheckbox && onRowCheckbox(realIdx)}
                                />
                            </TableCell>
                            <TableCell>{client.id ?? realIdx + 1}</TableCell>
                            {['firstName', 'lastName', 'email', 'company'].map((field) => {
                                const value = clients[realIdx][field];
                                const isEmail = field === 'email';

                                // Validation logic
                                const isEmpty = !value?.trim();
                                const isEmailInvalid = isEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
                                const hasError = (isEmail && isEmailInvalid) || (!isEmail && isEmpty);
                                let fieldLabel = field;
                                if (field === 'firstName') fieldLabel = 'First name';
                                else if (field === 'lastName') fieldLabel = 'Last name';
                                else if (field === 'company') fieldLabel = 'Company';

                                const helperText = isEmail
                                    ? (isEmailInvalid ? 'Invalid email format' : '')
                                    : (isEmpty ? `${fieldLabel} is required` : '');

                                return (
                                    <TableCell
                                        key={field}
                                        onClick={() => { if (isEditable) { startEditing(realIdx, field); } }}
                                        style={{ cursor: isEditable ? 'pointer' : 'none', position: 'relative' }}
                                    >
                                        {editIdx === realIdx && editField === field ? (
                                            <TextField
                                                value={value}
                                                onChange={(e) => handleChange(e, realIdx, field)}
                                                onBlur={stopEditing}
                                                autoFocus
                                                error={hasError}
                                                helperText={hasError ? helperText : ''}
                                            />
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', }}>
                                                <span>{value}&nbsp;</span>
                                                {hasError && (
                                                    <Tooltip title={helperText} placement='top'>
                                                        <Box sx={{ color: '#F6A904' }}><IconInfoCircle size="sm" /></Box>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        )}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    );
                })}
        </TableBody>
    );
};

export default EditableTableBody;